import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { ZafaraFullLogo } from "@/components/landing/Logo";

const LINKS = [
  { to: "/", label: "Accueil", testid: NAV.linkHome },
  { to: "/catalogue", label: "Catalogue", testid: "nav-link-catalogue" },
  { to: "/pourquoi-zafara", label: "Pourquoi Zafara", testid: NAV.linkWhy },
  { to: "/application", label: "L'application", testid: NAV.linkApp },
  { to: "/fondateur", label: "Le fondateur", testid: NAV.linkFounder },
  { to: "/faq", label: "FAQ", testid: "nav-link-faq" },
  { to: "/contact", label: "Contact", testid: NAV.linkContact },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FBFAF6]/90 border-b border-[#EDE7D9] shadow-[0_4px_20px_rgba(14,122,59,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link
            to="/"
            data-testid={NAV.logo}
            aria-label="Retour à l'accueil"
            className="group transition-transform hover:-translate-y-0.5"
          >
            <ZafaraFullLogo />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`text-sm font-medium transition-colors ${
                  isActive(l.to)
                    ? "text-[#0E7A3B]"
                    : "text-[#16241B]/85 hover:text-[#0E7A3B]"
                }`}
              >
                {l.label}
                {isActive(l.to) && (
                  <span className="block h-0.5 w-full mt-1 bg-[#F5A800] rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <button
              data-testid={NAV.ctaDownload}
              onClick={() => navigate("/telecharger")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#F5A800] px-5 py-2.5 text-sm font-semibold text-[#16241B] shadow-[0_8px_24px_rgba(245,168,0,0.35)] hover:bg-[#0E7A3B] hover:text-white hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(14,122,59,0.35)] transition-all duration-300"
            >
              <Download className="h-4 w-4" />
              Télécharger l'app
            </button>
          </div>

          <button
            data-testid={NAV.mobileMenuToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#EDE7D9] bg-white text-[#16241B]"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 border-t border-[#EDE7D9] pt-4 space-y-2">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setOpen(false)}
                className={`block w-full text-left text-base font-medium py-2.5 ${
                  isActive(l.to) ? "text-[#0E7A3B]" : "text-[#16241B]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/telecharger"
              onClick={() => setOpen(false)}
              className="mt-3 block w-full text-center rounded-full bg-[#F5A800] px-5 py-3 text-sm font-semibold text-[#16241B]"
            >
              Télécharger l'app
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};
